import {HTML_CLASS_HIDE_ELEMENT} from '../const/html';

export class HtmlHelper {
  private static _instance: HtmlHelper | null = null;
  private _document: Document = window.document;
  private _window: Window = window;

  private constructor() {
    return;
  }

  public static get instance(): HtmlHelper {
    if (!HtmlHelper._instance) HtmlHelper._instance = new HtmlHelper();
    return HtmlHelper._instance;
  }

  public get document(): Document {
    return this._document;
  }

  public get window(): Window {
    return this._window;
  }

  /**
   * We can use this to replace a real browser document with a fake one
   * for example in the test environment. If this isn't used, the default
   * browser document would be used.
   * @param document
   */
  public setDocument(document: Document) {
    this._document = document;
  }

  /**
   * We can use this to replace a real browser windiw with a fake one
   * for example in the test environment. If this isn't used, the default
   * browser window would be used.
   * @param window
   */
  public setWindow(window: Window) {
    this._window = window;
  }

  public loadScript(scriptUrl: string): HTMLScriptElement | true {
    const scripts = this.document.getElementsByTagName('script');

    for (let i = 0; i < scripts.length; ++i) {
      if (
        scripts[i].getAttribute('src') !== null &&
        scripts[i].getAttribute('src') === scriptUrl
      ) {
        return true;
      }
    }

    const node = this.document.createElement('script');
    node.src = scriptUrl;
    node.type = 'text/javascript';
    node.async = false;
    this.document.getElementsByTagName('head')[0].appendChild(node);

    return node;
  }

  public async loadScript2(scriptUrl: string): Promise<HTMLScriptElement> {
    const scripts = this.document.getElementsByTagName('script');

    for (let i = 0; i < scripts.length; ++i) {
      if (
        scripts[i].getAttribute('src') !== null &&
        scripts[i].getAttribute('src') === scriptUrl
      ) {
        return scripts[i];
      }
    }

    const node = this.document.createElement('script');
    node.src = scriptUrl;
    node.type = 'text/javascript';
    node.async = false;
    this.document.getElementsByTagName('head')[0].appendChild(node);

    return new Promise(resolve => {
      node.onload = () => {
        resolve(node);
      };
    });
  }

  public loadStyle(
    styleUrl: string,
    additionalAttributes: Record<string, string> = {},
  ): HTMLStyleElement | true {
    const styles = this.document.querySelectorAll('link[rel="stylesheet"]');

    for (let i = 0; i < styles.length; ++i) {
      if (
        styles[i].getAttribute('src') !== null &&
        styles[i].getAttribute('src') === styleUrl
      ) {
        return true;
      }
    }

    const node = this.document.createElement('link');
    node.rel = 'stylesheet';
    node.type = 'text/css';
    node.href = styleUrl;
    for (const attKey in additionalAttributes) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (node as any)[attKey] = additionalAttributes[attKey];
      console.log('attr', attKey, additionalAttributes[attKey]);
    }
    this.document.getElementsByTagName('head')[0].appendChild(node);

    return node;
  }

  public getUrlQueryParams(
    searchString = window.location.search,
  ): Record<string, string> {
    const pl = /\+/g; // Regex for replacing addition symbol with a space
    const search = /([^&=]+)=?([^&]*)/g;
    const decode = (s: string) => decodeURIComponent(s.replace(pl, ' '));
    const query = searchString.substring(1);

    const urlQueryParams: Record<string, string> = {};
    let match;
    while ((match = search.exec(query))) {
      urlQueryParams[decode(match[1])] = decode(match[2]);
    }

    return urlQueryParams;
  }

  public getUrlQueryParam(name: string): string | null {
    const qp = this.getUrlQueryParams();
    return qp[name] ?? null;
  }

  public buildSearchQuery(
    currentSearch: string,
    queryParameters: Record<string, string | number | boolean> = {},
  ): string {
    const params = this.getUrlQueryParams(currentSearch);
    const newParamsString = Object.entries({...params, ...queryParameters})
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return `?${newParamsString}`;
  }

  public hideElement(element: HTMLElement): void {
    element.classList.add(HTML_CLASS_HIDE_ELEMENT);
  }

  public showElement(element: HTMLElement): void {
    element.classList.remove(HTML_CLASS_HIDE_ELEMENT);
  }

  public getElementByDataAttribute(
    attribute: string,
    value: string,
  ): HTMLElement | null {
    return this.document.querySelector(`[${attribute}='${value}']`);
  }

  public getElementsByDataAttribute(
    attribute: string,
    value: string,
  ): HTMLElement[] {
    return Array.from(
      this.document.querySelectorAll(`[${attribute}='${value}']`),
    );
  }

  public redirectTo(url: string) {
    this._window.location.href = url;
  }
}
